/**
 * Google Maps Loader (clean, DI-friendly)
 *
 * - No direct dependency on browser globals (window/document injected)
 * - Supports callback-based Google Maps initialization
 * - Lazy singleton promise (prevents duplicate loads)
 * - Safe callback interception + restoration
 * - Handles both callback and non-callback script loading
 *
 * @param {Object} deps
 * @param {Function} deps.getGlobal   - Returns global object (e.g. () => window)
 * @param {Function} deps.getDocument - Returns document object (e.g. () => document)
 *
 * @returns {Function} load({ src, onLoad, onError }) => Promise<google>
 */

export default function createGoogleMapsLoader({
  getGlobal,
  getDocument,
} = {}) {
  if (typeof getGlobal !== 'function') {
    throw new Error('Missing getGlobal dependency.');
  }

  if (typeof getDocument !== 'function') {
    throw new Error('Missing getDocument dependency.');
  }

  let promise;

  function getCallbackName(src) {
    try {
      return new URL(src).searchParams.get('callback');
    } catch {
      return null;
    }
  }

  function loadScript({ src, resolve, reject, onLoad, onError, onScriptInjected }) {
    const globalObj = getGlobal();
    const doc = getDocument();
    const callbackName = getCallbackName(src);

    let settled = false;

    const resolveOnce = () => {
      if (settled) { return; }

      const google = globalObj.google;

      if (!google?.maps) {
        const error = new Error('Google Maps initialized but not available.');
        settled = true;
        onError?.(error);
        reject(error);
        return;
      }

      settled = true;
      onLoad?.(google);
      resolve(google);
    };

    if (callbackName) {
      const original = globalObj[callbackName];

      globalObj[callbackName] = function (...args) {
        try {
          resolveOnce();
        } finally {
          globalObj[callbackName] = original;

          if (typeof original === 'function') {
            original(...args);
          }
        }
      };
    }

    // Check if script is already present to avoid duplicates
    // If another feature already injected the script, reuse it and wait for it to finish.
    const existingScript = doc.getElementById('nhs_GoogleMapScript');
    if (existingScript) {
      // A pre-existing tag means a load is already in flight, regardless of which
      // caller created it — signal callers waiting on "fetch started" the same as
      // the fresh-injection path below, or they'll never know loading began.
      onScriptInjected?.();

       existingScript.addEventListener('load', () => {
        if (!callbackName) {
          resolveOnce();
        }
      }, { once: true });
      
      existingScript.addEventListener('error', () => {
        if (!settled) {
          settled = true;
          const error = new Error('Google Maps failed to load.');
          onError?.(error);
          reject(error);
        }
      }, { once: true });

      return;
    }

    const script = doc.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.id = 'nhs_GoogleMapScript';

    script.onload = () => {
      if (!callbackName) {
        resolveOnce();
      }
    };

    script.onerror = () => {
      if (settled) { return; }

      settled = true;
      const error = new Error('Google Maps failed to load.');
      onError?.(error);
      reject(error);
    };

    onScriptInjected?.();
    doc.head.appendChild(script);
  }

  return function load({ src, onLoad, onError, onScriptInjected } = {}) {
    if (!src) {
      return Promise.reject(
        new Error('Google Maps source URL is required.')
      );
    }

    const globalObj = getGlobal();

    if (globalObj.google?.maps) {
      // Maps is already fully loaded (another widget got there first) — still signal
      // "fetch started" so callers gating on that (e.g. the fetch-on-mapLibraryFetch
      // epic) don't miss it, since loadScript() is never reached in this branch.
      onScriptInjected?.();
      onLoad?.(globalObj.google);
      return Promise.resolve(globalObj.google);
    }

    if (promise) {
      return promise;
    }

    promise = new Promise((resolve, reject) => {
      loadScript({ src, resolve, reject, onLoad, onError, onScriptInjected });
    });

    return promise;
  };
}